import { formatDate } from '@frontend/mwm-ui'
import { useQuery } from '@tanstack/react-query'
import classNames from 'classnames'
import { isArray } from 'highcharts'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { MdOutlineFlag } from 'react-icons/md'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'

import type { ReactElement } from 'react'
import type { COHORT_TYPE, IStateReducer } from '~/types/compare-cohorts-context'

import { getLookersFilters } from '~/api/lookers'
import Button from '~/components/Buttons/Button'
import DateInput from '~/components/Inputs/Basic/DateInput'
import MultiSelectInput from '~/components/Inputs/Basic/MultiSelectInput'
import SelectInput from '~/components/Inputs/Basic/SelectInput'
import { LOCAL_STORAGE, MAX_DAYS_RANGE, TODAY } from '~/const'
import { useAppSummary } from '~/providers/AppSummary'
import { useCompareCohortsContext } from '~/providers/CompareCohorts'
import { semverComparator } from '~/utils/comparators'
import {
  clearURLParamsAndLocalStorageForCompare,
  setCohortsLocalStorageParams,
  setURLParamsForCohorts,
} from '~/utils/compare-cohorts'
import { getFullNameCountries } from '~/utils/countries'
import { startsWithFilter } from '~/utils/options'

import './styles.scss'

const getOptionFromFilters = (filters: string[], options: IOption[]): IOption[] => {
  return options.filter(({ value }) => filters.includes(value))
}

const getOptions = (arr: string[], sort?: 'ASC' | 'DESC', isSemver?: boolean): { label: string; value: string }[] => {
  const options = arr.map((val) => ({ label: val.toString(), value: val.toString() }))

  if (isSemver) {
    const optionsSortedAsc = options.sort((a, b) => semverComparator(a.label, b.label))
    return sort === 'DESC' ? optionsSortedAsc.reverse() : optionsSortedAsc
  }

  return sort === 'ASC'
    ? options.sort((a, b) => a.label.localeCompare(b.label))
    : sort === 'DESC'
      ? options.sort((a, b) => b.label.localeCompare(a.label))
      : options
}

const getBreakdowns = (state: IStateReducer) => {
  const breakdowns = []
  if (state.filters.breakdown?.[0] === 'abtest') {
    breakdowns.push({ key: 'abtest_key', value: [] })
  } else if (state.filters.breakdown?.[0] === 'dsv3') {
    breakdowns.push({ key: 'placement_key', value: state.filters.placementKey || [] })
  }

  return breakdowns
}

interface Props {
  cohort: COHORT_TYPE
}

const CompareCohortsFilters = ({ cohort }: Props): ReactElement => {
  /** Local state */

  const location = useLocation()

  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search])

  const navigate = useNavigate()

  const { dispatch, state } = useCompareCohortsContext()

  const { appKey, deviceType } = useAppSummary()

  const [currentApp, setCurrentApp] = useState({ appKey, deviceType })

  const [searchParams] = useSearchParams()

  const navigationGraphIdFromURL = searchParams.get('navigationGraphId')

  /** Queries & Mutations */

  const { data: filtersOptions, isLoading: isFiltersOptionsLoading } = useQuery<{
    data: { data: unknown[]; name: string }[] | null
  }>({
    queryFn: () =>
      getLookersFilters(appKey, deviceType, {
        dateRange: state[cohort].computedFiltersDate.range,
        filters: getBreakdowns(state[cohort]),
      }),
    queryKey: ['compare-cohorts-filters', appKey, deviceType, getBreakdowns(state[cohort])],
  })

  const genericGetOptions = useCallback(
    (filterName: string, sort?: 'ASC' | 'DESC', isSemver?: boolean) =>
      getOptions(
        (filtersOptions?.data?.find(({ name }) => name === filterName)?.data || []) as string[],
        sort,
        isSemver
      ),
    [filtersOptions?.data]
  )

  const daiOptions = useMemo(() => genericGetOptions('dai'), [genericGetOptions])
  const installationAppVersionOptions = useMemo(
    () => genericGetOptions('installation_app_version', 'DESC', true),
    [genericGetOptions]
  )
  const installationCountriesOptions = useMemo(
    () =>
      genericGetOptions('installation_country', 'ASC')
        .map((option) => ({
          ...option,
          icon: <span className={classNames('option-flag-icon fi', { [`fi-${option.label.toLowerCase()}`]: true })} />,
          label: option.value === 'ROTW' ? 'Rest of the World' : getFullNameCountries([option.value]).toString(),
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [genericGetOptions]
  )
  const deviceCategoryOptions = useMemo(() => genericGetOptions('device_category', 'ASC'), [genericGetOptions])
  const uaSourceOptions = useMemo(() => genericGetOptions('ua_source', 'ASC'), [genericGetOptions])
  const userTypeOptions = useMemo(() => genericGetOptions('user_type', 'ASC'), [genericGetOptions])
  const abtestKeyOptions = useMemo(() => genericGetOptions('abtest_key', 'ASC'), [genericGetOptions])
  const placementKeyOptions = useMemo(() => genericGetOptions('placement_key', 'ASC'), [genericGetOptions])
  const navigationGraphIdOptions = useMemo(() => genericGetOptions('navigation_graph_id'), [genericGetOptions])

  const setFilters = useCallback(
    (filters: Record<string, Record<string, string[]>>) => {
      dispatch({ cohort, payload: filters, type: 'filters' })
      setCohortsLocalStorageParams(filters, LOCAL_STORAGE.COMPARE_COHORTS_FILTERS)
      setURLParamsForCohorts(cohort, filters, queryParams, navigate)
    },
    [cohort, dispatch, navigate, queryParams]
  )

  /** Effects */

  useEffect(() => {
    if ((appKey && appKey !== currentApp.appKey) || (deviceType && deviceType !== currentApp.deviceType)) {
      if (currentApp.appKey && currentApp.deviceType) {
        dispatch({ cohort, type: 'resetFilters' })
      }

      setCurrentApp({ appKey, deviceType })
    }
  }, [appKey, deviceType, currentApp, dispatch, cohort])

  useEffect(() => {
    if (
      state[cohort].filters.breakdown?.[0] === 'dsv3' &&
      !navigationGraphIdFromURL &&
      navigationGraphIdOptions.length
    ) {
      dispatch({
        cohort,
        payload: {
          navigationGraphId: navigationGraphIdOptions.map(({ value }) => value),
        },
        type: 'filters',
      })
    }
  }, [cohort, dispatch, navigationGraphIdFromURL, navigationGraphIdOptions, state])

  /** Handlers */

  const handleFilterChanged = (filterName: string, selectedOptions: IOption | IOption[] | null) => {
    if (selectedOptions === null) {
      setFilters({ [cohort]: { [filterName]: [] } })
    } else if (isArray(selectedOptions)) {
      const values = (selectedOptions as IOption[]).map((option) => option.value)
      const filters = { [cohort]: { [filterName]: values } }

      setFilters(filters)
    } else {
      const filters = { [cohort]: { [filterName]: [(selectedOptions as IOption).value] } }

      setFilters(filters)
    }
  }

  const handleDatePickerChanged = (dateRange: string[]) => {
    const [startDate, endDate] = dateRange

    setFilters({
      [cohort]: {
        endDate: [formatDate(endDate, 'YYYY-MM-DD')],
        startDate: [formatDate(startDate, 'YYYY-MM-DD')],
      },
    })
  }

  const handleResetAll = () => {
    clearURLParamsAndLocalStorageForCompare(cohort, location, navigate, LOCAL_STORAGE.COMPARE_COHORTS_FILTERS)
    dispatch({ cohort, type: 'resetFilters' })
  }

  return (
    <div className="compare-cohorts-filters">
      <div className="compare-cohorts-filters__date-picker-wrapper">
        <DateInput
          className="from-period"
          endDate={state[cohort].filters.endDate?.[0]}
          excludeToday
          maxDate={TODAY}
          maxRangeDays={MAX_DAYS_RANGE}
          onDateRangeChange={handleDatePickerChanged}
          startDate={state[cohort].filters.startDate?.[0]}
          withFilters
          withRange
        />
      </div>
      <div className="compare-cohorts-filters__multi-filters-row">
        {state[cohort].filters.kpi?.includes('dai') || state[cohort].filters.breakdown?.includes('dai') ? (
          <MultiSelectInput
            className="filter-dropdown"
            isLoading={isFiltersOptionsLoading}
            label="DAI"
            onChange={(value: unknown) => handleFilterChanged('dai', value as IOption[])}
            options={daiOptions}
            placeholder="DAI"
            value={getOptionFromFilters(state[cohort].filters.dai || [], daiOptions)}
            withOnly
          />
        ) : (
          <SelectInput
            className="filter-dropdown"
            isLoading={isFiltersOptionsLoading}
            label="DAI"
            onChange={(value: unknown) => handleFilterChanged('dai', value as IOption[])}
            options={daiOptions}
            placeholder="DAI"
            value={getOptionFromFilters(state[cohort].filters.dai || [], daiOptions)}
          />
        )}
        <MultiSelectInput
          className="filter-dropdown"
          isLoading={isFiltersOptionsLoading}
          label="Installation App Version"
          onChange={(value: unknown) => handleFilterChanged('installationAppVersion', value as IOption[])}
          options={installationAppVersionOptions}
          placeholder="Installation App version"
          value={getOptionFromFilters(
            state[cohort].filters.installationAppVersion || [],
            installationAppVersionOptions
          )}
          withOnly
        />
        <MultiSelectInput
          className="filter-dropdown"
          defaultIcon={<MdOutlineFlag size={16} />}
          filterOption={startsWithFilter}
          isLoading={isFiltersOptionsLoading}
          label="Installation Countries"
          menuClassName="analytics-acquisition-view-countries-dropdown-menu"
          onChange={(value: unknown) => handleFilterChanged('installationCountry', value as IOption[])}
          optionType="Countries"
          options={installationCountriesOptions}
          placeholder="All Installation Countries"
          value={getOptionFromFilters(state[cohort].filters.installationCountry || [], installationCountriesOptions)}
          withOnly
        />
      </div>
      <div className="compare-cohorts-filters__multi-filters-row">
        <MultiSelectInput
          className="filter-dropdown"
          isLoading={isFiltersOptionsLoading}
          label="Device category"
          onChange={(value: unknown) => handleFilterChanged('deviceCategory', value as IOption[])}
          options={deviceCategoryOptions}
          placeholder="Device category"
          value={getOptionFromFilters(state[cohort].filters.deviceCategory || [], deviceCategoryOptions)}
          withOnly
        />
        <MultiSelectInput
          className="filter-dropdown"
          isLoading={isFiltersOptionsLoading}
          label="UA source"
          onChange={(value: unknown) => handleFilterChanged('uaSource', value as IOption[])}
          options={uaSourceOptions}
          placeholder="UA source"
          value={getOptionFromFilters(state[cohort].filters.uaSource || [], uaSourceOptions)}
          withOnly
        />
        <MultiSelectInput
          className="filter-dropdown"
          isLoading={isFiltersOptionsLoading}
          label="User type"
          onChange={(value: unknown) => handleFilterChanged('userType', value as IOption[])}
          options={userTypeOptions}
          placeholder="User type"
          value={getOptionFromFilters(state[cohort].filters.userType || [], userTypeOptions)}
          withOnly
        />
      </div>
      {state[cohort].filters.breakdown?.[0] === 'abtest' ? (
        <SelectInput
          className="filter-dropdown"
          isClearable
          isLoading={isFiltersOptionsLoading}
          label="abtest key"
          onChange={(value: unknown) => handleFilterChanged('abtestKey', value as IOption[])}
          options={abtestKeyOptions}
          placeholder="abtest_key"
          required
          value={getOptionFromFilters(state[cohort].filters.abtestKey || [], abtestKeyOptions)}
        />
      ) : null}
      {state[cohort].filters.breakdown?.[0] === 'dsv3' ? (
        <>
          <SelectInput
            className="filter-dropdown"
            isClearable
            isLoading={isFiltersOptionsLoading}
            label="Placement key"
            onChange={(value: unknown) => handleFilterChanged('placementKey', value as IOption[])}
            options={placementKeyOptions}
            placeholder="placement_key"
            required
            value={getOptionFromFilters(state[cohort].filters.placementKey || [], placementKeyOptions)}
          />
          <MultiSelectInput
            className="filter-dropdown navigation-graph-id-select-input"
            isLoading={isFiltersOptionsLoading}
            label="Navigation Graph Id"
            menuClassName="navigation-graph-id-select-input__menu"
            onChange={(value: unknown) => handleFilterChanged('navigationGraphId', value as IOption[])}
            options={navigationGraphIdOptions}
            placeholder="navigation_graph_id"
            value={getOptionFromFilters(state[cohort].filters.navigationGraphId || [], navigationGraphIdOptions)}
            withOnly
          />
        </>
      ) : null}
      <div className="actions-container">
        <Button className="reset-button" ghost onClick={handleResetAll}>
          Reset all
        </Button>
      </div>
    </div>
  )
}

export default CompareCohortsFilters
